import Axios from 'axios';
import config from 'utils/constants/config';
import { history } from 'root/routes';
import { ROUTES } from 'utils/constants/routes';

const http = Axios.create({
  baseURL: config.API_URL,
});

const serializeParams = (params = {}) => {
  return Object.entries(params)
    .map(([param, value]) => {
      return value !== undefined
        ? param +
            '=' +
            (Array.isArray(value)
              ? value.filter(Boolean).join('&' + param + '=')
              : value)
        : null;
    })
    .filter(Boolean)
    .join('&');
};

http.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (
      error &&
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      try {
        const oldRefreshToken = localStorage.getItem('refresh_token');
        http.defaults.headers.common['Authorization'] = '';
        const response = await postRefreshToken(oldRefreshToken);
        const { access_token, refresh_token } = response && response.data;
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        http.defaults.headers.common[
          'Authorization'
        ] = `Bearer ${access_token}`;
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return http(originalRequest);
      } catch (e) {
        history.push(ROUTES.LOGOUT);
        return Promise.reject(e);
      }
    }
    return Promise.reject(error);
  }
);

export const fetcher = (url) => http.get(`${url}`);
export const put = http.put;

export const postAuthToken = (phone, password) => {
  return http.post('auth/token/', {
    grant_type: 'password',
    client_id: config.CLIENT_ID,
    username: phone,
    password,
  });
};

export const postRefreshToken = (refresh_token) => {
  return http.post('auth/token/', {
    grant_type: 'refresh_token',
    refresh_token,
    client_id: config.CLIENT_ID,
  });
};

export const postAccessGateway = (token) =>
  http.post('fleetapp/access_gateway/', { token });

export const getProfile = () => {
  return http.get('mobile/profile/');
};

export const putTermsAndConditionsAccepted = () =>
  http.put(`mobile/profile/terms_and_conditions/`, {
    terms_and_conditions_accepted: true,
  });

export const getOffers = () => {
  return http.get('mobile/offers/?page_size=999');
};

export const getOfferDetail = (id) => `mobile/offers/${id}/`;

export const getDriverOfferDetail = (id) => {
  return `mobile/offers/${id}/driver_offer/`;
};

export const getJobDetail = (id) => {
  return http.get(`mobile/journeys/${id}/`);
};

export const getJobDetailUrl = (id) => {
  return `mobile/journeys/${id}/`;
};

export const putAcceptOffer = (id) => {
  return http.put(`mobile/offers/${id}/accept/`);
};

export const putRejectOffer = (id) => {
  return http.put(`mobile/offers/${id}/reject/`);
};

export const putBidOffer = (id, bidAmount) => {
  return http.put(`mobile/offers/${id}/bid/`, {
    bid_amount: bidAmount,
  });
};

export const getPendingJobs = () => {
  return http.get(
    `mobile/journeys/?${serializeParams({
      page_size: 0,
      status: 'PENDING',
    })}`
  );
};

export const getInTransitJobs = () => {
  return http.get(
    `mobile/journeys/?${serializeParams({
      page_size: 0,
      status: 'IN_TRANSIT_ONLY',
    })}`
  );
};

export const getDoneJobs = () =>
  `mobile/journeys/?${serializeParams({
    page_size: 25,
    status: ['TODO', 'DONE', 'CANCELLED_WITH_EARNINGS'],
  })}`;

export const getAllJobs = `mobile/journeys/?${serializeParams({
  page_size: 100,
  status: ['TODO', 'DONE', 'CANCELLED_WITH_EARNINGS'],
})}`;

export const putAssignDriver = (jobId, driverId) => {
  return http.put(`mobile/journeys/${jobId}/reassign/`, {
    driver: driverId,
  });
};

export const postAssignVehicles = (jobId, vehiclesIds) => {
  return http.post(`mobile/journeys/${jobId}/vehicles/`, {
    vehicles: vehiclesIds,
  });
};

export const getVehiclesSuitability = (jobId) => {
  return http.get(`mobile/journeys/${jobId}/vehicles_suitability/?page_size=0`);
};

export const postPushSubscription = (data) => {
  return http.post(`device/web_push/`, data);
};

export const postRequestResetPwd = (phone) => {
  return http.post(`/mobile/request_reset_pwd/`, {
    credential: phone,
    source: 'fleetapp',
  });
};

export const postResetPwd = (token, password) => {
  return http.post(`/mobile/reset_pwd/`, {
    token,
    password,
  });
};

export const getConversations = 'ticketing/conversations/';

export const getMessages = (conversationId) =>
  http.get(
    `${config.API_URL}ticketing/conversations/${conversationId}/messages/`
  );

export const getConversation = (conversationId) =>
  http.get(`${config.API_URL}ticketing/conversations/${conversationId}/`);

export const putMessage = (conversationId, message) =>
  http.put(
    `${config.API_URL}ticketing/conversations/${conversationId}/messages/`,
    {
      message: message,
    }
  );

export const postCreateConversation = (subject, journeyId) => {
  const data = journeyId ? { subject, journey_id: journeyId } : { subject };
  return http.post(`${config.API_URL}ticketing/conversations/`, data);
};

export const putAdvanceJourney = (journeyId, stop_id) => {
  return http.put(`mobile/journeys/${journeyId}/advance/`, { stop_id });
};

export const getShipmentOrder = (journeyId, lang) =>
  http.get(`journeys/${journeyId}/order_pdf/?lang=${lang}`, {
    responseType: 'blob',
  });

export const patchPasswordChange = (user_id, user_password, current_password) =>
  http.patch(`users/${user_id}/passwd_change/`, {
    user_password,
    current_password,
  });

export const putCommercialCommunications = (accepted) =>
  http.put(`mobile/profile/commercial_communications/`, {
    commercial_communications_accepted: accepted,
  });

export const getDriverStatus = (id) => `fleetapp/driver_status/${id}`;

export const getDoneWithoutManifestJourneys = `mobile/journeys/?${serializeParams(
  {
    status: ['DONE_WITHOUT_MANIFEST'],
    page_size: 0,
  }
)}`;

export const postDocumentUpload = (uuid, entity_type, document_type, files) =>
  http.post(`documents/upload/`, {
    uuid,
    entity_type,
    document_type,
    ...(files && { files }),
  });

export const postImageUpload = (uuid, entity_type, document_type, image) =>
  http.post(`documents/images/`, {
    uuid,
    entity_type,
    document_type,
    ...(image && { image }),
  });

export const deleteImages = (uuid, entity_type, document_type) =>
  http.delete(`documents/images/`, {
    data: { uuid, entity_type, document_type },
  });

export const patchUserLanguage = (user_id, language) =>
  http.patch(`users/${user_id}/`, {
    preferences: {
      language,
    },
  });

export const postCreateVehicle = (data) => http.post('vehicles/', data);

export const getCarrierProfile = (carrierUuid) =>
  http.get(`documents/profile/${carrierUuid}/`);

export const putCarrierProfile = (carrierUuid, carrierData) =>
  http.put(`documents/profile/${carrierUuid}/`, carrierData);

export const putCarrierProfileConditionsAccepted = (carrierUuid) =>
  http.put(`documents/profile/${carrierUuid}/terms_and_conditions/`, {
    terms_and_conditions_accepted: true,
  });

export const getCarrierDocumentsByUuid = (uuid) =>
  `documents/?${serializeParams({ q: uuid })}`;

export const getDriverDocumentsByUuid = (uuid) =>
  `documents/entity/driver/${uuid}/`;

export const getVehicleDocumentsByUuid = (uuid) =>
  `documents/entity/vehicle/${uuid}/`;

export default http;
